import { graphql, Link } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { FC } from 'react'
import Bio from '../components/Bio'
import Head from '../components/Head'
import Layout from '../components/layout'
import Widget from '../components/widget'
import { getSlugOfCategory } from '../custom-hooks/useCategories'
import { getPostsByCategory, getPostsWithTag } from '../custom-hooks/usePosts'
import { getSlugOfTag } from '../custom-hooks/useTags'
import { getGatsbyImage } from '../helpers/processImages'
import { MarkdownQueryI, AllFileQueryI, PageContextI } from '../types/common'
import { PostI, SiteMetadataI } from '../types/data-models'

interface MassivelyI {
  data: {
    site: {
      siteMetadata: SiteMetadataI
    }
    content: MarkdownQueryI<PostI>
    coverImages: AllFileQueryI
    thumbnails: AllFileQueryI
  }
  location: Location
  pageContext: PageContextI
}

const Massively: FC<MassivelyI> = ({ data, location, pageContext }) => {
  const post = data.content
  const fm = post.frontmatter
  const { previous, next } = pageContext
  const isPage = post.fields.type === 'PAGE'
  const isCategory = post.fields.type === 'CATEGORY'
  const isTag = post.fields.type === 'TAG'
  const isPost = post.fields.type === 'POST'
  const { isBioVisible } = data.site.siteMetadata

  return (
    <Layout location={location} data={data} isSinglePost={true}>
      <Head description={post.excerpt} title={fm.title} />
      <section>
        <header className="main content">
          {fm.date && <span className="date">{fm.date}</span>}
          <h1>{fm.title}</h1>
          {fm.category && (
            <a href={getSlugOfCategory(fm.category)} style={{ borderBottom: 'none' }}>
              <button>
                <span style={{ fontStyle: 'italic' }}>{fm.category}</span>
              </button>
            </a>
          )}
        </header>
        <div className="image main">
          {fm.coverImage?.url && (
            <GatsbyImage
              image={getGatsbyImage(fm.coverImage.url, data.coverImages)}
              alt={fm.coverImage?.label ?? 'Cover Image'}
            />
          )}
        </div>
        {post.html && <div className="content" dangerouslySetInnerHTML={{ __html: post.html }} />}
        {isPost && <hr />}
        {!isPage && (
          <footer className="post-footer">
            {fm.tags && fm.tags?.length > 0 && (
              <div style={{ marginBottom: '3rem' }}>
                {fm.tags.map((tag) => (
                  <React.Fragment key={tag}>
                    <a href={getSlugOfTag(tag)} style={{ borderBottom: 'none' }}>
                      <button style={{ borderRadius: '1rem', margin: '0 1rem' }}>{tag}</button>
                    </a>
                  </React.Fragment>
                ))}
              </div>
            )}
            {isBioVisible && <Bio />}
          </footer>
        )}
        {isPost && (
          <ul
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              listStyle: 'none',
              padding: 0,
            }}
          >
            <li>
              {previous && (
                <Link to={previous.fields.slug} rel="prev">
                  ← {previous.frontmatter.title}
                </Link>
              )}
            </li>
            <li>
              {next && (
                <Link to={next.fields.slug} rel="next">
                  {next.frontmatter.title} →
                </Link>
              )}
            </li>
          </ul>
        )}
      </section>
      {isCategory && (
        <React.Fragment>
          {getPostsByCategory(fm.title).map((category) => (
            <Widget
              key={category.node.fields.slug}
              post={category.node}
              thumbnail={
                category.node.frontmatter.coverImage?.url
                  ? getGatsbyImage(category.node.frontmatter.coverImage?.url, data.thumbnails)
                  : undefined
              }
            />
          ))}
        </React.Fragment>
      )}
      {isTag && (
        <React.Fragment>
          {getPostsWithTag(fm.title).map((tag) => (
            <Widget
              key={tag.node.fields.slug}
              post={tag.node}
              thumbnail={
                tag.node.frontmatter.coverImage?.url
                  ? getGatsbyImage(tag.node.frontmatter.coverImage?.url, data.thumbnails)
                  : undefined
              }
            />
          ))}
        </React.Fragment>
      )}
    </Layout>
  )
}

export default Massively

export const query = graphql`
  query($slug: String!) {
    site {
      siteMetadata {
        title
        description
        logo
        author
        isBioVisible
        social {
          twitter
          facebook
          snapchat
          instagram
          medium
        }
        menuLinks {
          label
          url
        }
        contact {
          info
          email
          tel
          address
        }
      }
    }
    content: markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      excerpt
      fields {
        type
        slug
      }
      frontmatter {
        isDraft
        isFeatured
        title
        subtitle
        category
        tags
        duration {
          label
          days
        }
        coverImage {
          label
          url
        }
      }
    }
    coverImages: allFile(filter: { sourceInstanceName: { eq: "images" } }) {
      edges {
        node {
          relativePath
          sourceInstanceName
          childImageSharp {
            gatsbyImageData(width: 1200, quality: 100, placeholder: BLURRED)
          }
          publicURL
        }
      }
    }
    thumbnails: allFile(filter: { sourceInstanceName: { eq: "images" } }) {
      edges {
        node {
          relativePath
          sourceInstanceName
          childImageSharp {
            gatsbyImageData(width: 480, quality: 100, placeholder: BLURRED)
          }
          publicURL
        }
      }
    }
  }
`
